import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Briefcase, School, Sparkles, ArrowRight, BookUser } from 'lucide-react';
import { Button } from '../ui/button';
import { BookDemoModal } from './BookDemoModal';

export const HeroSection = () => {
  const navigate = useNavigate();
  const [showDemoModal, setShowDemoModal] = useState(false);

  return (
    <div className="relative bg-gradient-to-br from-[#041f96]/5 via-white to-[#041f96]/5 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#041f96] rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#041f96]/70 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Find Your Perfect <span className="text-[#041f96]">Tutor</span> Now
              </h1>
              <p className="text-lg text-gray-600">
                Connect with qualified tutors, explore job opportunities, and discover top educational institutions
              </p>
            </div>

            {/* Stats Pills */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#041f96]/20">
                <Users className="h-5 w-5 text-[#041f96]" />
                <span className="text-sm font-semibold text-gray-700">7,000+ Tutors</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#041f96]/20">
                <Briefcase className="h-5 w-5 text-[#041f96]" />
                <span className="text-sm font-semibold text-gray-700">20,000+ Jobs</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#041f96]/20">
                <School className="h-5 w-5 text-[#041f96]" />
                <span className="text-sm font-semibold text-gray-700">100+ Institutes</span>
              </div>
            </div>

            {/* Book Free Demo CTA */}
            <div>
              <button
                onClick={() => setShowDemoModal(true)}
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#041f96] to-[#0633d4] text-white text-lg font-bold rounded-2xl shadow-xl shadow-blue-200/50 hover:shadow-2xl hover:shadow-blue-300/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              >
                <BookUser className="h-6 w-6 group-hover:rotate-12 transition-transform duration-300" />
                Book Free Demo
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#041f96] to-[#0633d4] opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300" />
              </button>
              <p className="text-sm text-gray-500 mt-3 ml-1">
                Tell us your requirements — we'll find the best tutor for you
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/find-tutor')}
                className="bg-[#041f96] hover:bg-[#031580]"
              >
                Find a Tutor
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/find-job')}
                className="border-2 border-[#041f96] text-[#041f96] hover:bg-[#041f96]/5"
              >
                Explore Jobs
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative hidden lg:block">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1588873281272-14886ba1f737"
                alt="Online Tutoring"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">50,000+</p>
                    <p className="text-sm text-gray-600">Students Served</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Book Demo Modal */}
      <BookDemoModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
};
